import { useCallback, useRef } from 'react';

import { useDebouncedCallback } from 'use-debounce';

/**
 * useDebouncedFetch
 *
 * Encapsula o padrão de debounce com fila de callbacks pós-fetch.
 *
 * Problema resolvido:
 *   Quando múltiplos eventos chegam ao mesmo tempo e cada um precisaria
 *   disparar um fetch, este hook agrupa todas as chamadas em um único fetch
 *   ao final da janela de debounce. Callbacks opcionais acumulados durante
 *   a espera são todos executados após o fetch completar.
 *
 * @param fetchFn   Função assíncrona de fetch a ser executada após o debounce.
 * @param delay     Tempo de debounce em ms (padrão: 1000ms).
 * @returns         `schedule(callback?)` — agenda o fetch e opcionalmente
 *                  registra um callback para rodar após o fetch completar.
 *
 * @example
 * const agendarFetch = useDebouncedFetch(() =>
 *   dispatch(fetchAtendimentosIniciados()),
 * );
 *
 * // Sem callback:
 * agendarFetch();
 *
 * // Com callback pós-fetch:
 * agendarFetch(() => dispatch(setAtendimentoSelecionado()));
 */
export function useDebouncedFetch(
  fetchFn: () => Promise<unknown>,
  delay = 1000,
) {
  const pendingCallbacksRef = useRef<Array<() => void>>([]);

  const debouncedFetch = useDebouncedCallback(() => {
    const callbacks = pendingCallbacksRef.current;
    pendingCallbacksRef.current = [];

    fetchFn().then(() => {
      callbacks.forEach(cb => cb());
    });
  }, delay);

  const schedule = useCallback(
    (callback?: () => void) => {
      if (callback) {
        pendingCallbacksRef.current.push(callback);
      }
      debouncedFetch();
    },
    [debouncedFetch],
  );

  return schedule;
}
